/**
 * Zod validation schemas for FileFormat entity JSONB fields
 *
 * These schemas provide runtime validation for the `metadata_json`, `schema_json`,
 * `mapping_json`, and `validation_rules_json` JSONB columns in the `file_formats` table.
 *
 * **Architecture**: Enum const arrays live in `file-format.types.ts`. These Zod object
 * schemas are the source of truth for JSONB shapes; TypeScript types are `z.infer<>`
 * of the matching schema and are re-exported from `file-format.types.ts`.
 *
 * **Usage Pattern**:
 * ```typescript
 * import type { FileFormatMetadata } from './file-format.types.js';
 * import { FileFormatMetadataSchema } from './file-format.schemas.js';
 *
 * const metadata: FileFormatMetadata = FileFormatMetadataSchema.parse(data);
 * ```
 *
 * Note: This is a comprehensive schema file with 78+ object schemas and 36+ enum validators.
 * Import only what you need to reduce bundle size.
 */
import { z } from 'zod';
import { ACCESS_CONTROL_LEVEL_VALUES, AML_ACTION_VALUES, APPROVAL_STATUS_VALUES, ARCHIVE_LOCATION_VALUES, BALANCE_SOURCE_VALUES, CHECKSUM_VALIDATION_VALUES, COMPOSITE_MAPPING_TYPE_VALUES, COMPRESSION_ALGORITHM_VALUES, CONDITIONAL_OPERATOR_VALUES, CONDITIONAL_RULE_TYPE_VALUES, CROSS_FIELD_VALIDATION_TYPE_VALUES, CURRENCY_MAPPING_TYPE_VALUES, DATA_CLASSIFICATION_VALUES, DELIMITER_VALUES, DUPLICATE_ACTION_VALUES, DUPLICATE_SCOPE_VALUES, 
// METADATA_JSON const arrays
ENCODING_VALUES, ENCRYPTION_ALGORITHM_VALUES, ERROR_SEVERITY_VALUES, FIELD_DATA_TYPE_VALUES, INSTITUTION_LOOKUP_SOURCE_VALUES, LINE_ENDING_VALUES, LOOKUP_TYPE_VALUES, NOTIFICATION_CHANNEL_VALUES, 
// VALIDATION_RULES_JSON const arrays
NULL_HANDLING_VALUES, PRIORITY_VALUES, // Used for ProcessingPriority, BusinessCriticality, and CPUPriority
REFERENCE_ACTION_VALUES, REFERENCE_TYPE_VALUES, REMEDIATION_ACTION_VALUES, ROUNDING_MODE_VALUES, SANCTIONS_ACTION_VALUES, 
// SCHEMA_JSON const arrays
SCHEMA_TYPE_VALUES, SCREENING_PROVIDER_VALUES, THRESHOLD_ACTION_VALUES, 
// MAPPING_JSON const arrays
TRANSFORMATION_TYPE_VALUES, VELOCITY_ACTION_VALUES, } from './file-format.types.js';
// ==================== METADATA_JSON SCHEMAS ====================
// Enum schemas derived from const arrays (single source of truth)
export const EncodingSchema = z.enum(ENCODING_VALUES);
export const DelimiterSchema = z.enum(DELIMITER_VALUES);
export const LineEndingSchema = z.enum(LINE_ENDING_VALUES);
export const ProcessingPrioritySchema = z.enum(PRIORITY_VALUES);
export const BusinessCriticalitySchema = z.enum(PRIORITY_VALUES);
export const DataClassificationSchema = z.enum(DATA_CLASSIFICATION_VALUES);
export const ArchiveLocationSchema = z.enum(ARCHIVE_LOCATION_VALUES);
export const CompressionAlgorithmSchema = z.enum(COMPRESSION_ALGORITHM_VALUES);
export const EncryptionAlgorithmSchema = z.enum(ENCRYPTION_ALGORITHM_VALUES);
export const CPUPrioritySchema = z.enum(PRIORITY_VALUES);
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNEL_VALUES);
export const SecurityScanningSchema = z.object({
    virus_scan_enabled: z.boolean().optional(),
    content_inspection: z.boolean().optional(),
    malware_detection: z.boolean().optional(),
    scan_timeout_seconds: z.number().int().positive().optional(),
});
export const ComplianceRequirementsSchema = z.object({
    pci_dss_required: z.boolean().optional(),
    sox_compliance: z.boolean().optional(),
    gdpr_compliance: z.boolean().optional(),
    hipaa_compliance: z.boolean().optional(),
    data_classification: DataClassificationSchema.optional(),
    encryption_required: z.boolean().optional(),
    retention_period_days: z.number().int().positive().optional(),
});
export const WorkflowIntegrationSchema = z.object({
    default_workflow_config: z.string().optional(),
    auto_start_workflow: z.boolean().optional(),
    workflow_timeout_minutes: z.number().int().positive().optional(),
    parallel_processing: z.boolean().optional(),
    batch_processing: z.boolean().optional(),
    batch_size: z.number().int().positive().optional(),
    checkpoint_frequency: z.number().int().positive().optional(),
    enable_rollback: z.boolean().optional(),
});
export const NotificationTemplatesSchema = z.object({
    success_template: z.string().optional(),
    error_template: z.string().optional(),
    quarantine_template: z.string().optional(),
});
export const NotificationSettingsSchema = z.object({
    success_notification: z.boolean().optional(),
    error_notification: z.boolean().optional(),
    quarantine_notification: z.boolean().optional(),
    sla_breach_notification: z.boolean().optional(),
    recipient_emails: z.array(z.string().email()).optional(),
    escalation_emails: z.array(z.string().email()).optional(),
    notification_channels: z.array(NotificationChannelSchema).optional(),
    webhook_urls: z.array(z.string().url()).optional(),
    notification_templates: NotificationTemplatesSchema.optional(),
});
export const LifecycleTransitionDaysSchema = z.object({
    to_infrequent_access: z.number().int().nonnegative().optional(),
    to_glacier: z.number().int().nonnegative().optional(),
    to_deep_archive: z.number().int().nonnegative().optional(),
});
export const ArchiveSettingsSchema = z.object({
    archive_after_processing: z.boolean().optional(),
    archive_on_error: z.boolean().optional(),
    retention_days: z.number().int().positive().optional(),
    archive_location: ArchiveLocationSchema.optional(),
    archive_path_template: z.string().optional(),
    compression_enabled: z.boolean().optional(),
    compression_algorithm: CompressionAlgorithmSchema.optional(),
    encryption_at_rest: z.boolean().optional(),
    encryption_algorithm: EncryptionAlgorithmSchema.optional(),
    lifecycle_transition_days: LifecycleTransitionDaysSchema.optional(),
});
export const PerformanceTuningSchema = z.object({
    memory_limit_mb: z.number().int().positive().optional(),
    cpu_priority: CPUPrioritySchema.optional(),
    connection_pool_size: z.number().int().positive().optional(),
    prefetch_rows: z.number().int().nonnegative().optional(),
    enable_caching: z.boolean().optional(),
    cache_size_mb: z.number().int().positive().optional(),
    buffer_size_kb: z.number().int().positive().optional(),
});
export const OutputSettingsSchema = z.object({
    file_name_template: z.string().optional(),
    s3_key_template: z.string().optional(),
    file_extension: z.string().optional(),
    mime_type: z.string().optional(),
    file_name_prefix: z.string().optional(),
});
export const FileFormatMetadataSchema = z.object({
    encoding: EncodingSchema.default('UTF-8'),
    delimiter: DelimiterSchema.optional(),
    line_ending: LineEndingSchema.optional(),
    header_present: z.boolean().optional(),
    header_line_number: z.number().int().nonnegative().optional(),
    data_start_line: z.number().int().nonnegative().optional(),
    quote_character: z.string().length(1).optional(),
    escape_character: z.string().length(1).optional(),
    max_file_size_mb: z.number().positive().optional(),
    max_record_count: z.number().int().nonnegative().optional(),
    allow_empty_fields: z.boolean().optional(),
    trim_whitespace: z.boolean().optional(),
    date_format: z.string().optional(),
    timezone: z.string().optional(),
    processing_priority: ProcessingPrioritySchema.default('NORMAL'),
    sla_processing_time_minutes: z.number().int().positive().default(1),
    business_criticality: BusinessCriticalitySchema.optional(),
    retry_attempts: z.number().int().nonnegative().optional(),
    retry_delay_seconds: z.number().int().nonnegative().optional(),
    exponential_backoff: z.boolean().optional(),
    error_threshold_percent: z.number().min(0).max(100).optional(),
    auto_quarantine_enabled: z.boolean().optional(),
    quarantine_on_structure_error: z.boolean().optional(),
    security_scanning: SecurityScanningSchema.optional(),
    compliance_requirements: ComplianceRequirementsSchema.optional(),
    workflow_integration: WorkflowIntegrationSchema.optional(),
    notification_settings: NotificationSettingsSchema.optional(),
    archive_settings: ArchiveSettingsSchema.optional(),
    performance_tuning: PerformanceTuningSchema.optional(),
    generator: z.string().optional(),
    processor: z.string().optional(),
    output_settings: OutputSettingsSchema.optional(),
    custom_properties: z.record(z.string(), z.unknown()).optional(),
});
// ==================== SCHEMA_JSON SCHEMAS ====================
export const SchemaTypeSchema = z.enum(SCHEMA_TYPE_VALUES);
export const FieldDataTypeSchema = z.enum(FIELD_DATA_TYPE_VALUES);
export const AccessControlLevelSchema = z.enum(ACCESS_CONTROL_LEVEL_VALUES);
export const FileStructureSchema = z.object({
    header_required: z.boolean().optional(),
    footer_required: z.boolean().optional(),
    delimiter: z.string().optional(),
    encoding: z.string().optional(),
    line_ending: LineEndingSchema.optional(),
});
export const SchemaDefinitionSchema = z.object({
    schema_type: SchemaTypeSchema,
    schema_version: z.string(),
    file_structure: FileStructureSchema.optional(),
});
export const FieldDefinitionSchema = z.object({
    field_name: z.string(),
    field_position: z.number().int().nonnegative().optional(),
    data_type: FieldDataTypeSchema,
    required: z.boolean().optional(),
    max_length: z.number().int().nonnegative().optional(),
    min_length: z.number().int().nonnegative().optional(),
    precision: z.number().int().positive().optional(),
    scale: z.number().int().nonnegative().optional(),
    default_value: z.string().optional(),
    allowed_values: z.array(z.string()).optional(),
    description: z.string().optional(),
});
export const SecurityRequirementsSchema = z.object({
    encryption_at_rest: z.boolean().optional(),
    encryption_in_transit: z.boolean().optional(),
    access_control_level: AccessControlLevelSchema.optional(),
    audit_logging: z.boolean().optional(),
    digital_signature_required: z.boolean().optional(),
});
export const VersionCompatibilitySchema = z.object({
    min_version: z.string().optional(),
    max_version: z.string().optional(),
    breaking_changes: z.array(z.string()).optional(),
});
export const ChangeHistorySchema = z.object({
    version: z.string().optional(),
    date: z.string().optional(),
    changes: z.string().optional(),
    author: z.string().optional(),
});
export const SchemaDocumentationSchema = z.object({
    schema_description: z.string().optional(),
    usage_examples: z.array(z.string()).optional(),
    change_history: z.array(ChangeHistorySchema).optional(),
});
export const MetadataSchemaSchema = z.object({
    version_compatibility: VersionCompatibilitySchema.optional(),
    documentation: SchemaDocumentationSchema.optional(),
});
export const FileFormatSchemaSchema = z.object({
    schema_definition: SchemaDefinitionSchema,
    field_definitions: z.array(FieldDefinitionSchema),
    security_requirements: SecurityRequirementsSchema.optional(),
    metadata_schema: MetadataSchemaSchema.optional(),
});
// ==================== MAPPING_JSON SCHEMAS ====================
export const TransformationTypeSchema = z.enum(TRANSFORMATION_TYPE_VALUES);
export const LookupTypeSchema = z.enum(LOOKUP_TYPE_VALUES);
export const CurrencyMappingTypeSchema = z.enum(CURRENCY_MAPPING_TYPE_VALUES);
export const CompositeMappingTypeSchema = z.enum(COMPOSITE_MAPPING_TYPE_VALUES);
export const ApprovalStatusSchema = z.enum(APPROVAL_STATUS_VALUES);
export const SourceFieldSchema = z.object({
    field_name: z.string(),
    field_position: z.number().int().nonnegative().optional(),
    field_alias: z.string().optional(),
});
export const TargetFieldSchema = z.object({
    table_name: z.string(),
    field_name: z.string(),
    field_type: z.string().optional(),
    required: z.boolean().optional(),
});
export const TransformationRuleSchema = z.object({
    transformation_type: TransformationTypeSchema,
    from_type: z.string().optional(),
    to_type: z.string().optional(),
    parameters: z.record(z.string(), z.unknown()).optional(),
});
export const ConditionalMappingSchema = z.object({
    condition_field: z.string().optional(),
    condition_operator: z.string().optional(),
    condition_value: z.unknown().optional(),
    true_mapping: z.record(z.string(), z.unknown()).optional(),
    false_mapping: z.record(z.string(), z.unknown()).optional(),
});
export const FieldMappingSchema = z.object({
    source_field: SourceFieldSchema,
    target_field: TargetFieldSchema,
    transformation_rules: z.array(TransformationRuleSchema).optional(),
    conditional_mapping: ConditionalMappingSchema.optional(),
});
export const LookupConfigSchema = z.object({
    table_name: z.string().optional(),
    key_column: z.string().optional(),
    value_column: z.string().optional(),
    static_mappings: z.record(z.string(), z.string()).optional(),
    cache_enabled: z.boolean().optional(),
    cache_ttl_seconds: z.number().int().nonnegative().optional(),
});
export const LookupTableSchema = z.object({
    lookup_name: z.string(),
    source_field: z.string(),
    lookup_type: LookupTypeSchema,
    lookup_config: LookupConfigSchema.optional(),
    default_value_on_not_found: z.string().optional(),
});
export const EnumerationMappingSchema = z.object({
    source_field: z.string(),
    target_field: z.string(),
    mappings: z.record(z.string(), z.string()),
    default_value: z.string().optional(),
    case_sensitive: z.boolean().optional(),
});
export const CurrencyMappingSchema = z.object({
    currency_field: z.string(),
    mapping_type: CurrencyMappingTypeSchema,
    mappings: z.record(z.string(), z.string()).optional(),
    default_currency: z.string().optional(),
});
export const ValueMappingsSchema = z.object({
    lookup_tables: z.array(LookupTableSchema).optional(),
    enumeration_mappings: z.array(EnumerationMappingSchema).optional(),
    currency_mappings: z.array(CurrencyMappingSchema).optional(),
});
export const CompositeMappingConfigSchema = z.object({
    delimiter: z.string().optional(),
    split_pattern: z.string().optional(),
    expression: z.string().optional(),
});
export const CompositeMappingSchema = z.object({
    mapping_type: CompositeMappingTypeSchema,
    source_fields: z.array(z.string()),
    target_field: z.object({
        table_name: z.string(),
        field_name: z.string(),
    }),
    mapping_config: CompositeMappingConfigSchema.optional(),
});
export const MappingChangeLogSchema = z.object({
    change_date: z.string().optional(),
    changed_by: z.string().optional(),
    change_description: z.string().optional(),
    change_reason: z.string().optional(),
});
export const MappingMetadataSchema = z.object({
    mapping_version: z.string().optional(),
    created_by: z.string().optional(),
    created_date: z.string().optional(),
    last_modified_by: z.string().optional(),
    last_modified_date: z.string().optional(),
    approval_status: ApprovalStatusSchema.optional(),
    approved_by: z.string().optional(),
    approval_date: z.string().optional(),
    change_log: z.array(MappingChangeLogSchema).optional(),
});
export const FileFormatMappingSchema = z.object({
    field_mappings: z.array(FieldMappingSchema),
    value_mappings: ValueMappingsSchema.optional(),
    composite_mappings: z.array(CompositeMappingSchema).optional(),
    metadata: MappingMetadataSchema.optional(),
});
// ==================== VALIDATION_RULES_JSON SCHEMAS ====================
export const NullHandlingSchema = z.enum(NULL_HANDLING_VALUES);
export const ChecksumValidationSchema = z.enum(CHECKSUM_VALIDATION_VALUES);
export const RoundingModeSchema = z.enum(ROUNDING_MODE_VALUES);
export const ErrorSeveritySchema = z.enum(ERROR_SEVERITY_VALUES);
export const RemediationActionSchema = z.enum(REMEDIATION_ACTION_VALUES);
export const ConditionalOperatorSchema = z.enum(CONDITIONAL_OPERATOR_VALUES);
export const ConditionalRuleTypeSchema = z.enum(CONDITIONAL_RULE_TYPE_VALUES);
export const CrossFieldValidationTypeSchema = z.enum(CROSS_FIELD_VALIDATION_TYPE_VALUES);
export const DuplicateScopeSchema = z.enum(DUPLICATE_SCOPE_VALUES);
export const DuplicateActionSchema = z.enum(DUPLICATE_ACTION_VALUES);
export const ReferenceTypeSchema = z.enum(REFERENCE_TYPE_VALUES);
export const ReferenceActionSchema = z.enum(REFERENCE_ACTION_VALUES);
export const InstitutionLookupSourceSchema = z.enum(INSTITUTION_LOOKUP_SOURCE_VALUES);
export const BalanceSourceSchema = z.enum(BALANCE_SOURCE_VALUES);
export const VelocityActionSchema = z.enum(VELOCITY_ACTION_VALUES);
export const ThresholdActionSchema = z.enum(THRESHOLD_ACTION_VALUES);
export const ScreeningProviderSchema = z.enum(SCREENING_PROVIDER_VALUES);
export const AMLActionSchema = z.enum(AML_ACTION_VALUES);
export const SanctionsActionSchema = z.enum(SANCTIONS_ACTION_VALUES);
// --- Field-level validation ---
export const DataTypeValidationSchema = z.object({
    strict_type_check: z.boolean().optional(),
    allow_implicit_conversion: z.boolean().optional(),
    null_handling: NullHandlingSchema.optional(),
});
export const FormatValidationSchema = z.object({
    pattern: z.string().optional(),
    case_sensitive: z.boolean().optional(),
    trim_before_validation: z.boolean().optional(),
    date_format: z.string().optional(),
    decimal_separator: z.enum(['.', ',']).optional(),
    thousands_separator: z.enum(['', ',', '.', ' ']).optional(),
    luhn_check: z.boolean().optional(),
    checksum_validation: ChecksumValidationSchema.optional(),
});
export const RangeValidationSchema = z.object({
    min_value: z.number().optional(),
    max_value: z.number().optional(),
    min_length: z.number().int().nonnegative().optional(),
    max_length: z.number().int().nonnegative().optional(),
    allowed_values: z.array(z.string()).optional(),
});
export const CurrencyValidationSchema = z.object({
    supported_currencies: z.array(z.string()).optional(),
    validate_decimal_places: z.boolean().optional(),
    currency_specific_rules: z.record(z.string(), z.unknown()).optional(),
});
export const BusinessValidationSchema = z.object({
    future_date_allowed: z.boolean().optional(),
    business_day_only: z.boolean().optional(),
    currency_validation: CurrencyValidationSchema.optional(),
});
export const ErrorConfigSchema = z.object({
    severity: ErrorSeveritySchema.optional(),
    error_message: z.string().optional(),
    error_code: z.string().optional(),
    remediation_action: RemediationActionSchema.optional(),
});
export const FieldValidationSchema = z.object({
    data_type_validation: DataTypeValidationSchema.optional(),
    format_validation: FormatValidationSchema.optional(),
    range_validation: RangeValidationSchema.optional(),
    business_validation: BusinessValidationSchema.optional(),
    error_config: ErrorConfigSchema.optional(),
});
// --- Value transformations ---
export const StringTransformationsSchema = z.object({
    trim_value: z.boolean().optional(),
    uppercase: z.boolean().optional(),
    lowercase: z.boolean().optional(),
    remove_spaces: z.boolean().optional(),
    remove_special_chars: z.boolean().optional(),
    pad_left: z
        .object({
        length: z.number().int().nonnegative(),
        character: z.string(),
    })
        .optional(),
    pad_right: z
        .object({
        length: z.number().int().nonnegative(),
        character: z.string(),
    })
        .optional(),
});
export const NumericTransformationsSchema = z.object({
    round_to_decimal_places: z.number().int().nonnegative().optional(),
    rounding_mode: RoundingModeSchema.optional(),
    absolute_value: z.boolean().optional(),
    negate_value: z.boolean().optional(),
});
export const TimezoneConversionSchema = z.object({
    from_timezone: z.string().optional(),
    to_timezone: z.string().optional(),
});
export const DateTransformationsSchema = z.object({
    date_format_input: z.string().optional(),
    date_format_output: z.string().optional(),
    timezone_conversion: TimezoneConversionSchema.optional(),
    add_days: z.number().int().optional(),
    add_business_days: z.number().int().optional(),
});
export const AccountNumberFormattingSchema = z.object({
    remove_dashes: z.boolean().optional(),
    pad_to_length: z.number().int().nonnegative().optional(),
});
export const CurrencyConversionSchema = z.object({
    from_currency: z.string().optional(),
    to_currency: z.string().optional(),
    conversion_rate_source: z.enum(['static', 'api', 'database']).optional(),
});
export const FintechTransformationsSchema = z.object({
    account_number_formatting: AccountNumberFormattingSchema.optional(),
    currency_conversion: CurrencyConversionSchema.optional(),
});
export const ValueTransformationSchema = z.object({
    string_transformations: StringTransformationsSchema.optional(),
    numeric_transformations: NumericTransformationsSchema.optional(),
    date_transformations: DateTransformationsSchema.optional(),
    fintech_transformations: FintechTransformationsSchema.optional(),
});
// --- Conditional / cross-field ---
export const ConditionalRuleSchema = z.object({
    condition_field: z.string(),
    condition_operator: ConditionalOperatorSchema,
    condition_value: z.unknown().optional(),
    target_field: z.string(),
    rule_type: ConditionalRuleTypeSchema,
    rule_config: z.record(z.string(), z.unknown()).optional(),
});
export const CrossFieldValidationSchema = z.object({
    validation_name: z.string(),
    fields: z.array(z.string()),
    validation_type: CrossFieldValidationTypeSchema,
    validation_config: z.record(z.string(), z.unknown()).optional(),
    severity: ErrorSeveritySchema.optional(),
    error_message: z.string(),
});
// --- Business rules ---
export const DuplicateDetectionSchema = z.object({
    enabled: z.boolean().optional(),
    duplicate_check_fields: z.array(z.string()).optional(),
    scope: DuplicateScopeSchema.optional(),
    action_on_duplicate: DuplicateActionSchema.optional(),
    time_window_hours: z.number().int().nonnegative().optional(),
});
export const ReferenceConfigSchema = z.object({
    table_name: z.string().optional(),
    column_name: z.string().optional(),
    cache_results: z.boolean().optional(),
});
export const ReferenceValidationSchema = z.object({
    field_name: z.string(),
    reference_type: ReferenceTypeSchema,
    reference_config: ReferenceConfigSchema.optional(),
    action_on_not_found: ReferenceActionSchema.optional(),
});
export const RoutingNumberValidationSchema = z.object({
    validate_checksum: z.boolean().optional(),
    validate_institution: z.boolean().optional(),
    institution_lookup_source: InstitutionLookupSourceSchema.optional(),
});
export const AccountNumberValidationSchema = z.object({
    validate_luhn: z.boolean().optional(),
    validate_format: z.boolean().optional(),
    validate_length: z.boolean().optional(),
});
export const BalanceValidationsSchema = z.object({
    check_sufficient_balance: z.boolean().optional(),
    balance_source: BalanceSourceSchema.optional(),
});
export const AccountValidationsSchema = z.object({
    routing_number_validation: RoutingNumberValidationSchema.optional(),
    account_number_validation: AccountNumberValidationSchema.optional(),
    balance_validations: BalanceValidationsSchema.optional(),
});
export const VelocityCheckSchema = z.object({
    check_name: z.string().optional(),
    time_window_hours: z.number().positive().optional(),
    max_transaction_count: z.number().int().positive().optional(),
    max_transaction_amount: z.number().positive().optional(),
    grouping_fields: z.array(z.string()).optional(),
    action_on_exceed: VelocityActionSchema.optional(),
});
export const AmountThresholdSchema = z.object({
    threshold_amount: z.number().positive().optional(),
    action: ThresholdActionSchema.optional(),
    reviewer_role: z.string().optional(),
});
export const TransactionValidationsSchema = z.object({
    velocity_checks: z.array(VelocityCheckSchema).optional(),
    amount_thresholds: z.array(AmountThresholdSchema).optional(),
});
export const FinancialValidationsSchema = z.object({
    account_validations: AccountValidationsSchema.optional(),
    transaction_validations: TransactionValidationsSchema.optional(),
});
export const AMLScreeningSchema = z.object({
    enabled: z.boolean().optional(),
    screening_threshold: z.number().optional(),
    screening_provider: ScreeningProviderSchema.optional(),
    screening_lists: z.array(z.string()).optional(),
    action_on_match: AMLActionSchema.optional(),
});
export const SanctionsScreeningSchema = z.object({
    enabled: z.boolean().optional(),
    screening_fields: z.array(z.string()).optional(),
    screening_provider: z.string().optional(),
    action_on_match: SanctionsActionSchema.optional(),
});
export const RegulatoryReportingSchema = z.object({
    ctr_threshold: z.number().optional(),
    sar_indicators: z.array(z.string()).optional(),
    auto_generate_reports: z.boolean().optional(),
});
export const ComplianceRulesSchema = z.object({
    aml_screening: AMLScreeningSchema.optional(),
    sanctions_screening: SanctionsScreeningSchema.optional(),
    regulatory_reporting: RegulatoryReportingSchema.optional(),
});
export const CompletenessCheckSchema = z.object({
    check_name: z.string().optional(),
    required_fields: z.array(z.string()).optional(),
    conditional_requirements: z.record(z.string(), z.unknown()).optional(),
    completeness_threshold: z.number().optional(),
});
export const ConsistencyCheckSchema = z.object({
    check_name: z.string().optional(),
    fields: z.array(z.string()).optional(),
    consistency_rule: z.string().optional(),
});
export const DataQualityRulesSchema = z.object({
    completeness_checks: z.array(CompletenessCheckSchema).optional(),
    consistency_checks: z.array(ConsistencyCheckSchema).optional(),
});
export const BusinessRulesSchema = z.object({
    duplicate_detection: DuplicateDetectionSchema.optional(),
    reference_validations: z.array(ReferenceValidationSchema).optional(),
    financial_validations: FinancialValidationsSchema.optional(),
    compliance_rules: ComplianceRulesSchema.optional(),
    data_quality_rules: DataQualityRulesSchema.optional(),
});
// --- Error handling ---
export const ErrorClassificationSchema = z.object({
    severity_levels: z.array(ErrorSeveritySchema).optional(),
    error_categories: z.array(z.string()).optional(),
});
export const ValidationErrorActionSchema = z.object({
    action: RemediationActionSchema.optional(),
    notification_required: z.boolean().optional(),
    escalation_threshold: z.number().optional(),
});
export const ComplianceErrorActionSchema = z.object({
    action: z.enum(['reject_file', 'quarantine', 'manual_review']).optional(),
    immediate_notification: z.boolean().optional(),
    escalation_roles: z.array(z.string()).optional(),
});
export const BusinessRuleErrorActionSchema = z.object({
    action: RemediationActionSchema.optional(),
    notification_required: z.boolean().optional(),
});
export const ErrorActionsSchema = z.object({
    validation_errors: ValidationErrorActionSchema.optional(),
    compliance_errors: ComplianceErrorActionSchema.optional(),
    business_rule_errors: BusinessRuleErrorActionSchema.optional(),
});
export const AutoCorrectionSchema = z.object({
    enabled: z.boolean().optional(),
    correction_rules: z.array(z.record(z.string(), z.unknown())).optional(),
    log_corrections: z.boolean().optional(),
});
export const RecoveryStrategiesSchema = z.object({
    auto_correction: AutoCorrectionSchema.optional(),
    fallback_values: z.record(z.string(), z.string()).optional(),
});
export const ErrorHandlingSchema = z.object({
    error_classification: ErrorClassificationSchema.optional(),
    error_actions: ErrorActionsSchema.optional(),
    recovery_strategies: RecoveryStrategiesSchema.optional(),
});
export const FileFormatValidationRulesSchema = z.object({
    field_validations: z.record(z.string(), FieldValidationSchema).optional(),
    value_transformations: z.record(z.string(), ValueTransformationSchema).optional(),
    conditional_rules: z.array(ConditionalRuleSchema).optional(),
    cross_field_validations: z.array(CrossFieldValidationSchema).optional(),
    business_rules: BusinessRulesSchema.optional(),
    error_handling: ErrorHandlingSchema.optional(),
});
//# sourceMappingURL=file-format.schemas.js.map